import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { ReportsList } from "../components/scouting/ReportsList";
import { CreateReportModal } from "../components/modals/CreateReportModal";
import ScoutingReportManager from "../managers/ScoutingReportsManager";
import { InfoBanner } from "../components/data/InfoBanner";
import AuthManager from "../managers/AuthManager";
import { LoadingState } from "../components/alerts/Alerts";
import { fetchAPI } from "../config/api";
import { DEFAULT_YEAR, toDivisionApi } from "../config/constants";

export const ScoutingPage = () => {
  const navigate = useNavigate();
  
  const [reports, setReports] = useState([]);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [selectedDivision, setSelectedDivision] = useState('ncaa_3');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isAuthReady, setIsAuthReady] = useState(false);

  const fetchTeams = useCallback(async (division, year) => {
    try {
      const response = await fetchAPI(`/api/teams?division=${division}&year=${year}`);
      return response || [];
    } catch (err) {
      console.error("Error fetching teams:", err);
      return [];
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      const unsubscribeAuth = AuthManager.onAuthStateChanged(async (user) => {
        if (!isMounted) return;

        try {
          if (!user) {
            const result = await AuthManager.anonymousSignIn();
            if (result.success && isMounted) {
              user = result.user;
            }
          }

          if (user) {
            const [userReports, teams] = await Promise.all([
              ScoutingReportManager.getUserReports(),
              fetchTeams('ncaa_3', DEFAULT_YEAR),
            ]);

            if (isMounted) {
              setReports(userReports);
              setAvailableTeams(teams);
            }
          }
        } catch (err) {
          console.error("Error in auth initialization:", err);
          toast.error("Failed to initialize. Please try again.");
        } finally {
          if (isMounted) {
            setLoading(false);
            setIsAuthReady(true);
          }
        }
      });

      return unsubscribeAuth;
    };

    const cleanup = initializeAuth();
    return () => {
      isMounted = false;
      cleanup.then((unsubscribe) => unsubscribe());
    };
  }, [fetchTeams]);

  const handleReportSelect = (report) => {
    navigate(`/scouting/${report.id}/scouting-report`);
  };

  const handleCreateReport = async (reportData) => {
    const loadingToast = toast.loading(
      reportData.addAllPlayers ? "Creating report and loading roster..." : "Creating report..."
    );
    try {
      let positionPlayers = [];
      let pitchers = [];

      if (reportData.addAllPlayers) {
        const [batters, pitcherData] = await Promise.all([
          fetchAPI(`/api/players_batting/${encodeURIComponent(reportData.teamName)}?year=${reportData.year}&division=${toDivisionApi(reportData.division)}`),
          fetchAPI(`/api/players_pitching/${encodeURIComponent(reportData.teamName)}?year=${reportData.year}&division=${toDivisionApi(reportData.division)}`),
        ]);
        
        if (batters && Array.isArray(batters)) {
          positionPlayers = batters.map(player => ({
            id: player.player_id,
            name: player.player_name,
            position: player.position || "",
          }));
        }
        
        if (pitcherData && Array.isArray(pitcherData)) {
          pitchers = pitcherData.map(player => ({
            id: player.player_id,
            name: player.player_name,
            position: player.position || "P",
          }));
        }
      }

      const finalReportData = {
        teamId: reportData.teamId,
        teamName: reportData.teamName,
        division: reportData.division,
        year: reportData.year,
        positionPlayers,
        pitchers,
        numHitters: positionPlayers.length,
        numPitchers: pitchers.length,
      };

      const newReport = await ScoutingReportManager.createReport(finalReportData);
      setReports(prev => [newReport, ...prev]);
      setIsCreateModalOpen(false);
      
      const message = reportData.addAllPlayers 
        ? `Report created with ${positionPlayers.length} hitters and ${pitchers.length} pitchers`
        : "Report created";
      toast.success(message, { id: loadingToast });
      
      navigate(`/scouting/${newReport.id}/scouting-report`);
    } catch (err) {
      console.error("Error creating report:", err);
      toast.error("Failed to create report", { id: loadingToast });
    }
  };

  const handleDeleteReport = async (reportId) => {
    const loadingToast = toast.loading("Deleting report...");
    try {
      await ScoutingReportManager.deleteReport(reportId);
      setReports(prev => prev.filter(r => r.id !== reportId));
      toast.success("Report deleted", { id: loadingToast });
    } catch (err) {
      console.error("Error deleting report:", err);
      toast.error("Failed to delete report", { id: loadingToast });
    }
  };

  const handleDivisionChange = async (division, year) => {
    setSelectedDivision(division);
    const teams = await fetchTeams(division, year);
    setAvailableTeams(teams);
  };

  if (!isAuthReady || loading) {
    return <LoadingState />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
      <div className="container max-w-6xl mx-auto px-8 sm:px-12 lg:px-16 py-16">
        <InfoBanner
          title="Scouting Reports & Player Analysis"
          description="Build comprehensive player reports with stats, notes, and evaluations. Create detailed scouting assessments for recruiting, player development, and team analysis. Track both position players and pitchers with custom metrics and observations."
        />

        <div className="relative z-10">
          <ReportsList
            reports={reports}
            onCreateClick={() => setIsCreateModalOpen(true)}
            onDeleteReport={handleDeleteReport}
            onReportSelect={handleReportSelect}
          />
        </div>

        <CreateReportModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={handleCreateReport}
          availableTeams={availableTeams}
          selectedDivision={selectedDivision}
          onDivisionChange={handleDivisionChange}
        />
      </div>
    </div>
  );
};